/**
 * 🎯 ECKPUNKT PROJECT CONFIGURATION
 *
 * Zentrale Konfiguration für das Eckpunkt Restaurant Projekt
 * Diese Config wird in main.ts geladen und per Vue.provide global verfügbar gemacht
 */
import eckpunktBranding from './branding.config';
export const eckpunktConfig = {
    // === PROJECT ===
    project: {
        id: 'eckpunkt',
        name: 'Eckpunkt Drink&Food',
        version: '2.0.0',
        environment: process.env.NODE_ENV === 'production' ? 'production' : 'development'
    },
    // === RESTAURANT ===
    restaurant: {
        name: 'Eckpunkt Drink&Food',
        shortName: 'Eckpunkt',
        description: 'Qualität und Geschmack in Nordhorn',
        longDescription: 'Willkommen beim Eckpunkt Drink&Food! Seit 1989 verwöhnen wir Sie mit hochwertigen Speisen und erstklassigem Service. Unsere Spezialität: Pizza, Pasta, Schnitzel und mehr!',
        cuisine: ['Pizza', 'Pasta', 'Deutsch', 'International'],
        priceRange: '€€',
        contact: {
            phone: '+49 1521 9287207',
            email: 'info@eckpunkt-nordhorn.de',
            website: 'https://www.eckpunkt-nordhorn.de'
        },
        address: {
            street: 'Gildehauser Weg',
            number: '23',
            postalCode: '48529',
            city: 'Nordhorn',
            country: 'Deutschland',
            countryCode: 'DE',
            coordinates: {
                lat: 52.4178253,
                lng: 7.07087757
            }
        },
        openingHours: {
            monday: { open: '14:00', close: '22:00', isOpen: true },
            tuesday: { open: '14:00', close: '22:00', isOpen: true },
            wednesday: { open: '14:00', close: '22:00', isOpen: true },
            thursday: { open: '14:00', close: '22:00', isOpen: true },
            friday: { open: '14:00', close: '23:00', isOpen: true },
            saturday: { open: '14:00', close: '22:00', isOpen: true },
            sunday: { open: '14:00', close: '21:30', isOpen: true }
        },
        social: {
            facebook: 'https://facebook.com/eckpunkt',
            instagram: 'https://instagram.com/eckpunkt'
        }
    },
    // === SEO ===
    seo: {
        title: 'Eckpunkt DrinK&Food - Lieferdienst & Dine-In in Nordhorn',
        titleTemplate: '{title} | Eckpunkt Nordhorn',
        description: 'Bestellen Sie online bei Eckpunkt Nordhorn. Pizza, Pasta, Schnitzel und mehr. Schnelle Lieferung und Abholung möglich.',
        keywords: [
            'eckpunkt',
            'nordhorn',
            'pizza',
            'pasta',
            'lieferservice',
            'restaurant',
            'essen bestellen',
            'online bestellen',
            'lieferung nordhorn'
        ],
        ogImage: '/assets/og-image.jpg',
        ogType: 'restaurant',
        twitterCard: 'summary_large_image',
        structuredData: {
            enabled: true,
            type: 'Restaurant',
            servesCuisine: ['Pizza', 'Pasta', 'German', 'International'],
            priceRange: '€€',
            paymentAccepted: ['Cash', 'Credit Card', 'PayPal'],
            image: ['/assets/restaurant-exterior.jpg', '/assets/restaurant-interior.jpg']
        },
        sitemap: {
            enabled: true,
            changefreq: 'weekly',
            priority: 0.8
        },
        robots: {
            index: true,
            follow: true
        }
    },
    // === BRANDING ===
    branding: {
        logo: {
            light: '/assets/logo-light.svg',
            dark: '/assets/logo-dark.svg',
            icon: '/assets/icon.png'
        },
        colors: {
            primary: '#DC2626', // Red-600
            primaryHover: '#B91C1C', // Red-700
            secondary: '#1F2937', // Gray-800
            accent: '#F59E0B', // Amber-500
            background: '#FFFFFF',
            text: '#1F2937'
        },
        fonts: {
            heading: 'Roboto, sans-serif',
            body: 'Inter, sans-serif'
        },
        images: {
            hero: '/assets/hero-image.jpg',
            about: '/assets/about-image.jpg',
            defaultProduct: '/assets/default-food-image.svg'
        },
        componentStyles: eckpunktBranding.componentStyles
    },
    ui: {
        productInteractions: {
            enableProductDetailsModal: false,
            enableRouteSync: false,
            sliderButtonLabel: 'In den Warenkorb',
            menuButtonLabel: 'In den Warenkorb'
        }
    },
    // === SERVICES ===
    services: {
        delivery: {
            enabled: true,
            radius: 10,
            minimumOrder: 1500, // 15,00 €
            fee: 300, // 3,00 €
            freeDeliveryFrom: 3000 // 30,00 €
        },
        pickup: {
            enabled: true,
            minimumOrder: 1000 // 10,00 €
        },
        tableReservation: {
            enabled: false,
            maxAdvanceDays: 14
        }
    },
    // === PRICING ===
    pricing: {
        currency: 'EUR',
        locale: 'de-DE',
        taxRate: 19,
        serviceFee: 50, // 0,50 €
        includesTax: true
    },
    // === PAYMENT ===
    payment: {
        cash: true,
        card: true,
        paypal: true,
        applePay: false,
        googlePay: false,
        bankTransfer: false
    },
    // === FEATURES ===
    features: {
        cart: true,
        checkout: true,
        orderTracking: true,
        loyaltyProgram: false,
        reviews: true,
        favorites: true,
        notifications: true,
        multilingual: false,
        darkMode: false
    },
    // === DEPLOYMENT ===
    deployment: {
        domains: {
            production: 'eckpunkt-nordhorn.de',
            staging: 'staging.eckpunkt-nordhorn.de',
            development: 'localhost:3020'
        },
        api: {
            baseUrl: process.env.API_BASE_URL || 'http://localhost:3030/api',
            publicUrl: process.env.PUBLIC_API_BASE_URL || 'http://localhost:3030/public',
            timeout: 10000
        },
        cdn: {
            url: 'https://cdn.eckpunkt-nordhorn.de',
            imageOptimization: true
        },
        analytics: {
            googleAnalytics: {
                enabled: true,
                measurementId: 'G-XXXXXXXXXX',
                anonymizeIp: true,
                cookieConsentCategory: 'analytics'
            },
            googleTagManager: {
                enabled: true,
                containerId: 'GTM-XXXXXXX',
                cookieConsentCategory: 'analytics'
            }
        }
    },
    // === LEGAL ===
    legal: {
        businessName: 'Eckpunkt Drink&Food',
        businessType: 'Einzelunternehmer',
        vatNumber: 'DE368020370',
        taxNumber: '',
        tradeRegister: '',
        owner: {
            name: 'Tobias Bensch',
            role: 'Inhaber'
        },
        pages: {
            imprint: '/impressum',
            privacy: '/impressum?tab=datenschutz',
            terms: '',
            cancellation: ''
        }
    },
    // === CONTENT ===
    content: {
        homepage: {
            hero: {
                title: 'Willkommen bei Eckpunkt Drink&Food',
                subtitle: 'Qualität und Geschmack seit 1989',
                cta: 'Jetzt bestellen',
                image: '/assets/hero-image.jpg'
            },
            features: [
                {
                    icon: 'truck',
                    title: 'Schnelle Lieferung',
                    description: 'Frisch und heiß zu Ihnen nach Hause'
                },
                {
                    icon: 'star',
                    title: 'Top Qualität',
                    description: 'Nur beste Zutaten für unsere Gerichte'
                },
                {
                    icon: 'clock',
                    title: 'Täglich geöffnet',
                    description: 'Bestellen Sie wann immer Sie Hunger haben'
                }
            ]
        },
        about: {
            title: 'Über uns',
            content: 'Seit 1989 verwöhnen wir unsere Gäste mit hochwertigen Speisen und erstklassigem Service...'
        }
    }
};
export default eckpunktConfig;
